import type { ReactNode } from 'react'

import { authClient } from '@/customer/lib/auth'

/**
 * Renders `children` only when the user has an active session.
 *
 * Returns `null` while `useSession` is fetching so we don't flash
 * the signed-out fallback on cold mounts: better-auth's atom sits at
 * `isPending: true, data: null` from initial render through to the
 * fetch resolving, and rendering the fallback in that window would
 * fire a spurious redirect on every page load.
 */
export function SignedIn({
  children,
  fallback = null,
}: {
  children: ReactNode
  fallback?: ReactNode
}) {
  const { data, isPending } = authClient.useSession()
  if (isPending) return null
  return data?.session ? children : fallback
}

/** Inverse of `SignedIn`; see that component for the loading-state rationale. */
export function SignedOut({
  children,
  fallback = null,
}: {
  children: ReactNode
  fallback?: ReactNode
}) {
  const { data, isPending } = authClient.useSession()
  if (isPending) return null
  return !data?.session ? children : fallback
}
